import { getDB } from "./schema.js";
import { matchesAllFilters } from "../utils/filters.js";
import { nowIso } from "../utils/dates.js";
import { queueGroupWrite } from "./sync.js";

const STORE = "groups";
const GAMES_STORE = "games";
const BOOKS_STORE = "books";
const TV_STORE = "tv";
const ANIME_STORE = "anime";
const MOVIES_STORE = "movies";
const COMICS_STORE = "comics";

// Which store an item id lives in, as the normalized media_type token (matches
// db/media.js). Returns null if the id isn't a tracked item (e.g. it's a nested
// child group, or a stale id).
async function lookupMediaType(db, id) {
	if (await db.get(GAMES_STORE, id)) return "videogame";
	if (await db.get(BOOKS_STORE, id)) return "book";
	if (await db.get(TV_STORE, id)) return "tv";
	if (await db.get(ANIME_STORE, id)) return "anime";
	if (await db.get(MOVIES_STORE, id)) return "movie";
	if (await db.get(COMICS_STORE, id)) return "comic";
	return null;
}

// A group's scope is derived from its members: all-one-type → that type, mixed
// or empty → null (cross-media). Scans the members, so callers that can update
// scope incrementally (addItemToGroup) should prefer that.
async function recomputeMediaType(db, memberIds) {
	const types = new Set();
	for (const id of memberIds ?? []) {
		const t = await lookupMediaType(db, id);
		if (t) types.add(t);
	}
	return types.size === 1 ? [...types][0] : null;
}

function uuid() {
	if (globalThis.crypto?.randomUUID) return crypto.randomUUID();
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

// Auto-grouping (create-on-add by franchise/collection/author) was retired — it
// buried hand-made groups. We no longer create auto-groups; the v9 migration
// deleted the pure ones and converted edited ("hybrid") ones to manual. This
// matcher is kept so any group that still carries an `auto_rule` (e.g. a device
// mid-upgrade) keeps resolving correctly; manual groups pass `null` and skip it.
// Raw game / movie / comic records don't carry media_type; normalized ones do.
function mediaTypeOf(item) {
	if (item.media_type) return item.media_type;
	if (item.igdb_id != null) return "videogame";
	if (item.imdb_id != null) return "movie";
	if (item.comic_key != null) return "comic";
	return null;
}

function matchesAutoRule(item, rule) {
	if (!rule) return false;
	// Smart collections (roadmap #56): a saved list filter, matched live.
	if (rule.type === "smart") {
		if (rule.media_type && mediaTypeOf(item) !== rule.media_type) return false;
		return (rule.filters?.length ?? 0) > 0 && matchesAllFilters(item, rule.filters);
	}
	if (rule.type === "franchise") return item.franchise === rule.value;
	if (rule.type === "collection") return item.collection === rule.value;
	if (rule.type === "author") return (item.authors ?? []).includes(rule.value);
	return false;
}

export async function listGroups() {
	const db = await getDB();
	return db.getAll(STORE);
}

export async function getGroupById(id) {
	const db = await getDB();
	return db.get(STORE, id);
}

// `member_ids` / `child_group_ids` seed membership at creation (e.g. drag-drop
// drops two items into a fresh group). `media_type` is the group's scope —
// which category list it shows up in; null = mixed/cross-media. New groups are
// NOT pinned to home by default — pinning is opt-in (a fresh group has no
// members yet, so we can't reliably guess "cross-media" at this point anyway).
export async function createManualGroup({
	name,
	description = null,
	member_ids = [],
	child_group_ids = [],
	media_type = null,
	pinned_to_home = false,
	series_ref = null,
	smart_rule = null,
}) {
	if (!name || !name.trim()) throw new Error("Group name is required");
	const now = nowIso();
	const record = {
		id: uuid(),
		name: name.trim(),
		description,
		kind: "manual",
		auto_rule: null,
		auto_key: null,
		manual_member_ids: Array.from(new Set(member_ids)),
		manual_exclude_ids: [],
		child_group_ids: Array.from(new Set(child_group_ids)),
		// Scope + home visibility (see Groups v2 design).
		media_type,
		pinned_to_home,
		cover_item_id: null,
		is_ordered: false,
		ordered_ids: null,
		// When this collection was created from a book series, the Wikidata series
		// id — lets the collection view show the rest of the series as dimmed
		// "not yet added" suggestions. Local-only for now (not synced to the cloud
		// groups table), so a cross-device pull may drop it — a follow-up.
		series_ref,
		// A smart collection's saved filter: { type: "smart", media_type, filters }.
		smart_rule,
		created_at: now,
		updated_at: now,
	};
	const db = await getDB();
	await db.add(STORE, record);
	await queueGroupWrite({ type: "create", record });
	return record;
}

export async function deleteGroup(id) {
	const db = await getDB();
	await db.delete(STORE, id);
	await queueGroupWrite({ type: "delete", record: { id } });
	// Detach from any parent so no group is left pointing at a missing child.
	for (const g of await db.getAll(STORE)) {
		if ((g.child_group_ids ?? []).includes(id)) {
			const next = {
				...g,
				child_group_ids: g.child_group_ids.filter((cid) => cid !== id),
				updated_at: nowIso(),
			};
			await db.put(STORE, next);
			await queueGroupWrite({ type: "update", record: next });
		}
	}
}

export async function updateGroup(id, patch) {
	const db = await getDB();
	const current = await db.get(STORE, id);
	if (!current) throw new Error(`Group ${id} not found`);
	const next = { ...current, ...patch, updated_at: nowIso() };
	await db.put(STORE, next);
	await queueGroupWrite({ type: "update", record: next });
	return next;
}

// Membership is stored as bare item ids (globally-unique UUIDs), so a group can
// hold games and books alike — these mutators don't care which store an id is in.
export async function addItemToGroup(groupId, itemId) {
	const db = await getDB();
	const group = await db.get(STORE, groupId);
	if (!group) throw new Error(`Group ${groupId} not found`);
	const next = { ...group, updated_at: nowIso() };
	const hadMembers = (next.manual_member_ids ?? []).length > 0;
	const manualSet = new Set(next.manual_member_ids ?? []);
	manualSet.add(itemId);
	next.manual_member_ids = Array.from(manualSet);
	next.manual_exclude_ids = (next.manual_exclude_ids ?? []).filter(
		(id) => id !== itemId,
	);
	if (group.kind === "auto") next.kind = "hybrid";

	// Keep scope in sync incrementally — only the new item's type matters: an
	// empty group takes the item's type; a same-type group is unchanged; adding a
	// different type makes it cross-media (null).
	const addedType = await lookupMediaType(db, itemId);
	if (!hadMembers) next.media_type = addedType;
	else if (group.media_type !== addedType) next.media_type = null;

	await db.put(STORE, next);
	await queueGroupWrite({ type: "update", record: next });
	return next;
}

export async function removeItemFromGroup(groupId, itemId) {
	const db = await getDB();
	const group = await db.get(STORE, groupId);
	if (!group) throw new Error(`Group ${groupId} not found`);
	const next = { ...group, updated_at: nowIso() };
	next.manual_member_ids = (next.manual_member_ids ?? []).filter(
		(id) => id !== itemId,
	);
	// If the item would be re-added by the group's auto-rule, record an explicit
	// exclude. The item could live in either store, so check both.
	const item =
		(await db.get(GAMES_STORE, itemId)) ?? (await db.get(BOOKS_STORE, itemId));
	if (item && ((group.auto_rule && matchesAutoRule(item, group.auto_rule)) || (group.smart_rule && matchesAutoRule(item, group.smart_rule)))) {
		const excludeSet = new Set(next.manual_exclude_ids ?? []);
		excludeSet.add(itemId);
		next.manual_exclude_ids = Array.from(excludeSet);
		if (group.kind === "auto") next.kind = "hybrid";
	}
	// Removing a member can narrow a mixed group back to a single type, so do a
	// full recompute here (removes are infrequent enough to afford the scan).
	next.media_type = await recomputeMediaType(db, next.manual_member_ids);
	await db.put(STORE, next);
	await queueGroupWrite({ type: "update", record: next });
	return next;
}

// Resolve a group's direct members across all media. The caller passes the
// combined, normalized media list (from db/media.js's listAllMedia) — groups.js
// stays free of a media.js import so there's no games→groups→media cycle. This
// is the pure/sync core; `resolveGroupMembers` is the async-friendly alias.
export function resolveGroupMembersSync(group, allMedia) {
	const media = allMedia ?? [];
	const excludeSet = new Set(group.manual_exclude_ids ?? []);
	const manualSet = new Set(group.manual_member_ids ?? []);

	const members = [];
	for (const m of media) {
		if (excludeSet.has(m.id)) continue;
		const inAuto = matchesAutoRule(m, group.auto_rule) || matchesAutoRule(m, group.smart_rule);
		const inManual = manualSet.has(m.id);
		if (inAuto || inManual) members.push(m);
	}

	if (group.is_ordered && Array.isArray(group.ordered_ids)) {
		const idx = new Map(group.ordered_ids.map((id, i) => [id, i]));
		members.sort((a, b) => {
			const ai = idx.has(a.id) ? idx.get(a.id) : Infinity;
			const bi = idx.has(b.id) ? idx.get(b.id) : Infinity;
			return ai - bi;
		});
	} else {
		members.sort((a, b) => (a.title ?? "").localeCompare(b.title ?? ""));
	}
	return members;
}

export async function resolveGroupMembers(group, allMedia) {
	return resolveGroupMembersSync(group, allMedia);
}

// ---- Nesting (child groups) ----------------------------------------------

// All item members of a group, including those reached through nested child
// groups, de-duplicated. Used for the rolled-up stats/cover/subtitle/count a
// top-level group shows. Cycle-safe via a visited set. `allGroups` is the full
// groups list; `allMedia` the normalized cross-media list.
export function subtreeMembers(group, allGroups, allMedia) {
	const byId = new Map(allGroups.map((g) => [g.id, g]));
	const seen = new Set();
	const out = new Map();
	const walk = (g) => {
		if (!g || seen.has(g.id)) return;
		seen.add(g.id);
		for (const m of resolveGroupMembersSync(g, allMedia)) out.set(m.id, m);
		for (const cid of g.child_group_ids ?? []) walk(byId.get(cid));
	};
	walk(group);
	return [...out.values()].sort((a, b) =>
		(a.title ?? "").localeCompare(b.title ?? ""),
	);
}

// Is `targetId` somewhere in `rootId`'s subtree (inclusive)? Used to reject
// nestings that would form a cycle.
function isInSubtree(rootId, targetId, byId, seen = new Set()) {
	if (rootId === targetId) return true;
	if (seen.has(rootId)) return false;
	seen.add(rootId);
	const g = byId.get(rootId);
	for (const cid of g?.child_group_ids ?? []) {
		if (isInSubtree(cid, targetId, byId, seen)) return true;
	}
	return false;
}

// Nest `childId` under `parentId`. Groups form a tree (one parent max), so the
// child is detached from any current parent. Rejects self-nesting and any cycle
// (nesting a group inside its own descendant).
export async function addChildGroup(parentId, childId) {
	if (parentId === childId) {
		throw new Error("A group can't contain itself.");
	}
	const db = await getDB();
	const all = await db.getAll(STORE);
	const byId = new Map(all.map((g) => [g.id, g]));
	const parent = byId.get(parentId);
	const child = byId.get(childId);
	if (!parent) throw new Error(`Group ${parentId} not found`);
	if (!child) throw new Error(`Group ${childId} not found`);
	// A cycle forms iff the parent is inside the child's subtree.
	if (isInSubtree(childId, parentId, byId)) {
		throw new Error("That would nest a group inside one of its own subgroups.");
	}
	// One parent per group: detach from any other parent first.
	for (const g of all) {
		if (g.id !== parentId && (g.child_group_ids ?? []).includes(childId)) {
			const detached = {
				...g,
				child_group_ids: g.child_group_ids.filter((id) => id !== childId),
				updated_at: nowIso(),
			};
			await db.put(STORE, detached);
			await queueGroupWrite({ type: "update", record: detached });
		}
	}
	const set = new Set(parent.child_group_ids ?? []);
	set.add(childId);
	const nextParent = {
		...parent,
		child_group_ids: [...set],
		updated_at: nowIso(),
	};
	await db.put(STORE, nextParent);
	await queueGroupWrite({ type: "update", record: nextParent });
	return nextParent;
}

export async function removeChildGroup(parentId, childId) {
	const db = await getDB();
	const parent = await db.get(STORE, parentId);
	if (!parent) return;
	const next = {
		...parent,
		child_group_ids: (parent.child_group_ids ?? []).filter(
			(id) => id !== childId,
		),
		updated_at: nowIso(),
	};
	await db.put(STORE, next);
	await queueGroupWrite({ type: "update", record: next });
}

// Create a new manual group and nest it directly under `parentId` (inheriting
// the parent's media scope as a starting point).
export async function createSubgroup(parentId, { name }) {
	const db = await getDB();
	const parent = await db.get(STORE, parentId);
	if (!parent) throw new Error(`Group ${parentId} not found`);
	const child = await createManualGroup({
		name,
		media_type: parent.media_type ?? null,
		pinned_to_home: false,
	});
	await addChildGroup(parentId, child.id);
	return child;
}

// The groups that should appear inline in a single media category's list (e.g.
// the Books list), each with its resolved members, plus the set of member ids
// those groups "collapse" out of the flat list (a 40k group stands in for its
// hundreds of books). A group qualifies when its `media_type` scope matches the
// category and it isn't nested inside another group (only top-level collections
// surface in the list; child groups show inside their parent). Pass the full
// groups list and the normalized cross-media list (db/media.js listAllMedia).
export function categoryGroups(allGroups, allMedia, mediaType) {
	const childIds = new Set(allGroups.flatMap((g) => g.child_group_ids ?? []));
	const cards = allGroups
		.filter((g) => g.media_type === mediaType && !childIds.has(g.id))
		.map((group) => ({
			// Card reflects the whole subtree (a 40k group counts the books inside
			// its Horus Heresy subgroup too).
			group,
			members: subtreeMembers(group, allGroups, allMedia),
		}))
		.sort((a, b) => (a.group.name ?? "").localeCompare(b.group.name ?? ""));

	// Collapse every item the shown groups represent — transitively, so a book
	// nested in a subgroup is also hidden from the flat list.
	const collapsedIds = new Set();
	for (const { members } of cards) {
		for (const m of members) collapsedIds.add(m.id);
	}
	return { cards, collapsedIds };
}

// Returns the groups that include this item (game or book), accounting for
// manual member/exclude overrides (and any residual auto-rule on a not-yet-
// migrated group).
export async function getGroupsContainingItem(item) {
	const all = await listGroups();
	return all.filter((g) => {
		if ((g.manual_exclude_ids ?? []).includes(item.id)) return false;
		if ((g.manual_member_ids ?? []).includes(item.id)) return true;
		return matchesAutoRule(item, g.auto_rule) || matchesAutoRule(item, g.smart_rule);
	});
}

export function summarizeMembers(members) {
	const total = members.length;
	const byEngagement = {};
	const awardCounts = {};
	let rated = 0;
	let ratingSum = 0;
	let ownedCount = 0;
	for (const g of members) {
		const eng = g.engagement_status ?? "haven_started";
		byEngagement[eng] = (byEngagement[eng] ?? 0) + 1;
		if (g.rating != null) {
			rated++;
			ratingSum += g.rating;
		}
		if (g.ownership_status === "have_it" || g.ownership_status === "had_it") {
			ownedCount++;
		}
		// Tally how many members carry each award, so a group can show e.g.
		// "Nostalgia pick ×3" across its contents.
		for (const a of g.awards ?? []) {
			awardCounts[a] = (awardCounts[a] ?? 0) + 1;
		}
	}
	const avgRating = rated > 0 ? ratingSum / rated : null;
	const doneCount = byEngagement.done ?? 0;
	return { total, byEngagement, avgRating, ownedCount, awardCounts, doneCount };
}
